import moment from "moment-timezone";

const MAX_OCCURRENCES = 100000;

const VALID_OFFSET_UNITS = new Set(["hour", "day", "week", "month", "year"]);

const IMPORT_REPEAT_UNITS = new Set(["Day", "Week", "Month", "Year"]);

function isValidMomentUnit(unitLower) {
  return VALID_OFFSET_UNITS.has(unitLower);
}

function workOrderCreationMoment(occurrence, leadAmount, leadUnitLower) {
  return occurrence.clone().subtract(leadAmount, leadUnitLower);
}

function coerceImportFieldValue(raw) {
  if (raw === null || raw === undefined) return raw;
  if (Array.isArray(raw) && raw.length > 0) {
    return coerceImportFieldValue(raw[0]);
  }
  if (typeof raw === "object" && !(raw instanceof Date)) {
    for (const key of ["value", "label", "text"]) {
      if (
        Object.prototype.hasOwnProperty.call(raw, key) &&
        raw[key] !== undefined &&
        raw[key] !== null
      ) {
        return raw[key];
      }
    }
  }
  return raw;
}

function parseOccurrenceCap(raw) {
  const v = coerceImportFieldValue(raw);
  if (v === undefined || v === null || v === "") return NaN;
  const n =
    typeof v === "number" ? v : Number(String(v).trim().replace(/,/g, ""));
  if (!Number.isFinite(n) || n <= 0) return NaN;
  return Math.floor(n);
}

export function isImportFrequencyRepeat(raw) {
  return (
    String(coerceImportFieldValue(raw) ?? "")
      .trim()
      .toLowerCase() === "repeat"
  );
}
export function normalizeRepetitionEndMode(raw) {
  const v = coerceImportFieldValue(raw);
  if (v === "onDate" || v === "afterOccurrences" || v === "never") {
    return v;
  }
  const t = String(v ?? "")
    .trim()
    .toLowerCase();
  if (t === "") return "never";
  if (t === "never") return "never";
  if (t === "ondate" || t === "on date") return "onDate";
  if (t === "afteroccurrences" || t === "after occurrences") {
    return "afterOccurrences";
  }
  return null;
}

export function countImmediateWorkOrders({
  eventStart,
  isRepeat,
  repeatEvery,
  repeatUnit,
  repetitionEnd: repetitionEndInput,
  repetitionEndValueDate,
  repetitionEndValueOccurrences,
  occurrencesCount,
  creationOffsetAmount,
  creationOffsetUnit,
  now = new Date(),
}) {
  const repetitionEnd =
    normalizeRepetitionEndMode(repetitionEndInput) ?? "never";

  const firstOccurrence = moment(eventStart);
  if (!firstOccurrence.isValid()) return 0;

  const leadUnit = String(creationOffsetUnit || "").toLowerCase();
  if (!isValidMomentUnit(leadUnit)) return 0;

  const leadAmount = Number(creationOffsetAmount);
  if (!Number.isFinite(leadAmount) || leadAmount <= 0) return 0;

  const referenceNow = moment(now);

  const stepUnit = String(repeatUnit || "day").toLowerCase();
  if (!isValidMomentUnit(stepUnit)) return 0;

  const stepAmount = Number(repeatEvery);
  if (isRepeat && (!Number.isFinite(stepAmount) || stepAmount <= 0)) {
    return 0;
  }

  const isAfterOccurrences = repetitionEnd === "afterOccurrences";
  const isOnDate = repetitionEnd === "onDate";
  const repeatCapParsed = parseOccurrenceCap(
    occurrencesCount ?? repetitionEndValueOccurrences,
  );
  const repeatCap =
    isAfterOccurrences &&
    Number.isFinite(repeatCapParsed) &&
    repeatCapParsed > 0
      ? repeatCapParsed
      : null;

  const endDateRaw = coerceImportFieldValue(repetitionEndValueDate);
  const repeatEndDate = isOnDate ? moment(endDateRaw) : null;
  const hasValidRepeatEndDate = Boolean(repeatEndDate?.isValid());
  const repeatEndDateLimit = hasValidRepeatEndDate
    ? repeatEndDate.clone().endOf("day")
    : null;

  let immediateCount = 0;
  let currentOccurrence = firstOccurrence.clone();
  const maxIterations = isRepeat
    ? repeatCap
      ? Math.min(MAX_OCCURRENCES, repeatCap)
      : MAX_OCCURRENCES
    : 1;

  for (let i = 0; i < maxIterations; i++) {
    if (
      isRepeat &&
      isOnDate &&
      hasValidRepeatEndDate &&
      currentOccurrence.isAfter(repeatEndDateLimit)
    ) {
      break;
    }

    const creation = workOrderCreationMoment(
      currentOccurrence,
      leadAmount,
      leadUnit,
    );

    if (creation.isAfter(referenceNow)) break;

    immediateCount += 1;
    if (!isRepeat) break;

    currentOccurrence.add(stepAmount, stepUnit);
  }

  return immediateCount;
}

export function countImmediateWorkOrdersFromForm(event) {
  const eventDate = event?.eventDate;
  const hasStructuredDate =
    eventDate?.day && eventDate?.month && eventDate?.year;
  const eventStart = hasStructuredDate
    ? moment({
        year: eventDate.year,
        month: eventDate.month - 1,
        day: eventDate.day,
      })
    : moment(eventDate);
  if (!eventStart.isValid()) return 0;

  const isRepeat = event.frequency === "repeat";
  const repeatEvery = isRepeat ? parseInt(event.repeatInNumber, 10) || 1 : 1;
  const repeatUnit = isRepeat ? event.repeatIn || "Week" : "Day";

  return countImmediateWorkOrders({
    eventStart,
    isRepeat,
    repeatEvery,
    repeatUnit,
    repetitionEnd: event.repetitionEnd,
    repetitionEndValueDate: event.repetitionEndValueDate,
    repetitionEndValueOccurrences: event.repetitionEndValueOccurrences,
    occurrencesCount: event.occurrencesCount,
    creationOffsetAmount: parseInt(event.ticketCreationNumber, 10) || 1,
    creationOffsetUnit: event.ticketCreationIn || "Day",
  });
}

export function countImmediateWorkOrdersFromImportFields({
  startDate,
  frequencyRaw,
  repeatEveryRaw,
  repeatUnitRaw,
  repetitionEndRaw,
  repetitionEndValueDateRaw,
  repetitionEndValueOccurrencesRaw,
  occurrencesCountRaw,
  autoCreateAmountRaw,
  autoCreateUnitRaw,
}) {
  if (
    !startDate ||
    !(startDate instanceof Date) ||
    Number.isNaN(startDate.getTime())
  ) {
    return 0;
  }

  const repetitionNormalized =
    normalizeRepetitionEndMode(repetitionEndRaw) ?? "never";
  const hasRepetitionEndSelection =
    repetitionNormalized === "onDate" ||
    repetitionNormalized === "afterOccurrences";
  const isRepeat =
    isImportFrequencyRepeat(frequencyRaw) || hasRepetitionEndSelection;

  const autoAmountCoerced = coerceImportFieldValue(autoCreateAmountRaw);
  const amount = Number(autoAmountCoerced);
  const unit = String(coerceImportFieldValue(autoCreateUnitRaw) ?? "").trim();
  if (
    autoAmountCoerced === undefined ||
    autoAmountCoerced === null ||
    String(autoAmountCoerced).trim() === "" ||
    Number.isNaN(amount) ||
    amount <= 0 ||
    !unit
  ) {
    return 0;
  }

  const repeatEveryCoerced = coerceImportFieldValue(repeatEveryRaw);
  const repeatEvery = isRepeat ? Number(repeatEveryCoerced) : 1;
  const repeatUnitTrim = isRepeat
    ? String(coerceImportFieldValue(repeatUnitRaw) ?? "").trim()
    : "Day";

  if (
    isRepeat &&
    (repeatEveryCoerced === undefined ||
      repeatEveryCoerced === null ||
      String(repeatEveryCoerced).trim() === "" ||
      Number.isNaN(repeatEvery) ||
      repeatEvery <= 0 ||
      !IMPORT_REPEAT_UNITS.has(repeatUnitTrim))
  ) {
    return 0;
  }

  return countImmediateWorkOrders({
    eventStart: startDate,
    isRepeat,
    repeatEvery,
    repeatUnit: repeatUnitTrim,
    repetitionEnd: repetitionNormalized,
    repetitionEndValueDate: coerceImportFieldValue(repetitionEndValueDateRaw),
    repetitionEndValueOccurrences: coerceImportFieldValue(
      repetitionEndValueOccurrencesRaw,
    ),
    occurrencesCount: coerceImportFieldValue(occurrencesCountRaw),
    creationOffsetAmount: amount,
    creationOffsetUnit: unit,
  });
}
